import React from "react";

export default function SeoGuideSection() {
  return (
    <section className="stf-section">
      <div className="stf-wrap">
        <div className="stf-seo-grid">
          <article className="stf-article">
            <div className="stf-kicker">Guide</div>
            <h2>Hiring retail security in Australia: what to know before you book</h2>
            <p>
              Retail security carries the same legal requirement as any other type of security work in Australia. Whether you're covering a single boutique on weekends or running loss prevention across a shopping centre, the rules around who can work your floor and what they're allowed to do are set by your state, not by the centre management and not by the security company quoting you.
            </p>
            <p>
              This guide covers what retailers most often get wrong when hiring store security, and what to check before you confirm anyone.
            </p>

            <h3 id="licence">Do you legally need a licensed security officer?</h3>
            <p>
              Yes, in every state. Loss prevention, floor presence, cash office watch and store lockup all count as licensable security work, even when the guard is working plain clothed. A staff member without a license can't be tasked with security duties.
            </p>
            <table>
              <thead>
                <tr>
                  <th>State</th>
                  <th>License commonly required</th>
                  <th>Regulator</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>New South Wales</td>
                  <td>Security License Class 1A, Security Officer</td>
                  <td>NSW Police SLED</td>
                </tr>
                <tr>
                  <td>Victoria</td>
                  <td>Private Security Individual Operator License, security guard activity</td>
                  <td>Victoria Police Licensing and Regulation Division</td>
                </tr>
                <tr>
                  <td>Queensland</td>
                  <td>Security Provider License Class 1, unarmed security officer</td>
                  <td>Office of Fair Trading</td>
                </tr>
                <tr>
                  <td>Western Australia</td>
                  <td>Security Officer license</td>
                  <td>WA Police Licensing</td>
                </tr>
                <tr>
                  <td>South Australia</td>
                  <td>Security Agents License, general guarding function</td>
                  <td>Consumer &amp; Business Services</td>
                </tr>
              </tbody>
            </table>
            <div className="stf-callout">
              <b>Worth knowing:</b> a security guard's powers on your shop floor are limited to what any member of the public has under citizen's arrest provisions, they can't search a customer or their bags without consent. Brief your guard on your store's own procedures before their first shift.
            </div>

            <h3 id="how-many">How many guards does your store need?</h3>
            <p>
              There's no single national ratio for retail. Most stores work from footprint and trading pattern, then adjust for risk:
            </p>
            <ul>
              <li><strong>Store size and layout:</strong> multiple exits or blind spots each add to what one guard can realistically cover.</li>
              <li><strong>Trading pattern:</strong> weekend and after work trade usually needs more cover than a quiet weekday morning.</li>
              <li><strong>Stock profile:</strong> high value or easily concealed stock raises the bar on loss prevention.</li>
              <li><strong>Seasonal peaks:</strong> sale periods and the lead up to Christmas are when most retailers add cover.</li>
              <li><strong>Cash handling:</strong> an opening or closing shift with a cash count is a different job to a floor walk.</li>
            </ul>
            <p>If your centre management or insurer has already set a minimum, treat that as the floor, not the target.</p>

            <h3 id="cost">What retail security costs</h3>
            <p>
              Rates for licensed retail guards in Australia generally sit between $33 and $48 an hour, depending on the work. The biggest factors are the day of week, whether it's a peak trading period, and whether the role is floor presence or dedicated loss prevention. Weekend and holiday season shifts sit at the top of that range.
            </p>
            <p>
              The other variable is who you book through. Agencies typically add a margin on top of the guard's rate for coordination and rostering. On Staffoo, guards set their own rate and you see it on their application, so the number you compare is the number you pay. You can see current averages in the pricing section above.
            </p>

            <h3 id="vs">Loss prevention officer or security officer, which do you need?</h3>
            <p>
              These overlap more than other industries, but the emphasis is different, and it's worth being specific in your job post.
            </p>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Loss Prevention Officer</th>
                  <th>Security Officer</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Typical work</td>
                  <td>Focuses on shoplifting deterrence, often plain clothed, working closely with store staff and CCTV.</td>
                  <td>Covers general floor presence, entry monitoring and store lockup.</td>
                </tr>
                <tr>
                  <td>Best for</td>
                  <td>Suits stores with a known shrinkage problem or high value stock.</td>
                  <td>Suits general floor presence, centre entrances and opening or closing duties.</td>
                </tr>
                <tr>
                  <td>Visibility</td>
                  <td>Often deliberately low profile.</td>
                  <td>Usually uniformed and visible as a deterrent.</td>
                </tr>
              </tbody>
            </table>

            <h3 id="checklist">What to check before you confirm a guard</h3>
            <ul>
              <li><strong>License class and expiry:</strong> that it matches the work and is current in your state.</li>
              <li><strong>Relevant experience:</strong> retail floor experience is a different job to office or event work.</li>
              <li><strong>Reviews from similar stores:</strong> volume matters less than whether the stores looked like yours.</li>
              <li><strong>Availability across peak periods:</strong> weekends and sale events are when you need cover most.</li>
              <li><strong>Who's responsible for insurance:</strong> and what happens if a guard can't make a shift.</li>
            </ul>
            <p>
              On Staffoo, license verification happens before a guard can apply, and the license class, experience and review history sit on every application you receive.
            </p>

            <h3 id="direct">Hiring direct instead of through an agency</h3>
            <p>
              Traditional agencies take your brief, roster guards internally and send you an invoice. You rarely know who's coming until the day, and you can't request the same person twice. Hiring direct through a platform flips that: you see who applied, you pick, and you can rebook the guards who worked out well. For a multi store retailer, that continuity across sites usually matters more than the hourly rate.
            </p>
            <p>
              If you're hiring for a different setting, the same process applies to corporate and office security, event security and construction site security.
            </p>

            <div className="stf-recap">
              <div className="stf-kicker">Before you post</div>
              <p>
                Confirm the license type your state requires, size your cover from footprint and trading pattern rather than a guess, and treat agency quotes as a ceiling. Staffoo shows you each guard's real rate before you confirm anyone.
              </p>
            </div>
          </article>

          {/* Sidebar Cards Column */}
          <aside className="stf-seo-aside">
            <div className="stf-toc">
              <h4>On this page</h4>
              <a href="#license">Do you need a licensed guard?</a>
              <a href="#how-many">How many guards you need</a>
              <a href="#cost">What it costs</a>
              <a href="#vs">Loss prevention vs security officer</a>
              <a href="#checklist">Pre booking checklist</a>
              <a href="#direct">Direct vs agency</a>
            </div>

            <div className="stf-side-cta">
              <h4>Know what you need?</h4>
              <p>Post the job free and start receiving applications from licensed retail security guards near you.</p>
              <a href="#" className="stf-btn stf-btn-solid stf-btn-block">Post a retail security job</a>
            </div>

            <div className="stf-aside-module">
              <h4>Related guides</h4>
              <a href="#">Licensing explained by state</a>
              <a href="#">How pricing works</a>
              <a href="#">Hiring for a business or agency</a>
              <a href="#">Corporate &amp; office security</a>
            </div>

            <div className="stf-aside-trust">
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Every applicant's license is checked before you see their profile.
              </div>
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M12 2v20M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"></path>
                </svg>
                Free to post, no lock in contract, pay after sign off.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
